import { useAccount, useMsal } from "@azure/msal-react";
import {
    Box,
    Button,
    Card,
    Container,
    Group,
    Radio,
    Stack,
    Text,
    Textarea,
    Tooltip,
    useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCircleCheck, IconClock, IconExternalLink } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useRef, useState } from "react";
import DatasourceRequestAccessModal from "src/components/DatasourceRequestAccessModal";
import { fetchPendingRequests } from "src/hooks/useFetchPendingRequests";
import { type Datasource, useUser } from "src/hooks/useUser";
import { postAPI } from "src/utils/fetch";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
interface Category {
    id: string;
    title: string;
    name?: string;
    subcategories: string[];
}

interface RadioCategoryItemProps {
    category: Category;
    selected: boolean;
    onSelect: (categoryId: string) => void;
    index: number;
    disabled?: boolean;
    dataSources?: Datasource[];
    selectedDataSources: Record<string, boolean>;
    onDataSourceToggle: (dataSourceId: string, selected: boolean) => void;
    availableDataSourcesForProductLine: Set<string>;
    requesting_access: boolean;
    pendingRequests: Set<string>;
    setPendingRequests: React.Dispatch<React.SetStateAction<Set<string>>>;
}

function RadioCategoryItem({
    category,
    selected,
    onSelect,
    disabled = false,
    dataSources = [],
    selectedDataSources,
    onDataSourceToggle,
    availableDataSourcesForProductLine,
    requesting_access,
    pendingRequests,
    setPendingRequests,
}: RadioCategoryItemProps) {
    const handleCardClick = () => {
        if (!disabled) {
            onSelect(category.id);
        }
    };
    const [accessRequestModalOpened, { open: openAccessRequest, close: closeAccessRequest }] =
        useDisclosure(false);

    const [selectedDatasource, setSelectedDatasource] = useState(null);

    const filteredDataSources = dataSources.filter(
        (ds) => category.id === "all" || availableDataSourcesForProductLine.has(ds.id),
    );
    const { colorScheme } = useMantineColorScheme();

    return (
        <div>
            <Card
                shadow="sm"
                p="lg"
                radius="md"
                withBorder
                onClick={handleCardClick}
                style={(theme) => ({
                    cursor: disabled ? "not-allowed" : "pointer",
                    borderColor: selected ? theme.colors.blue[5] : theme.colors.gray[3],
                    padding: theme.spacing.lg,
                    opacity: disabled ? 0.6 : 1,
                })}
            >
                <Box mb={8}>
                    <Group gap={10}>
                        <Radio
                            label={category.title}
                            size="xs"
                            checked={selected}
                            disabled={disabled}
                            onChange={(event) => {
                                event.stopPropagation();
                                if (!disabled) {
                                    onSelect(category.id);
                                }
                            }}
                            styles={(theme) => ({
                                input: {
                                    backgroundColor:
                                        colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
                                },
                                label: {
                                    fontWeight: 600,
                                },
                            })}
                        />
                        {disabled && (
                            <Text size="xs" c="dimmed" fs="italic">
                                (Not available for selected product line)
                            </Text>
                        )}
                    </Group>
                </Box>

                {selected && filteredDataSources.length > 0 && (
                    <Stack ml={24} mt={12} gap={6}>
                        <Group gap={8} align="center">
                            {filteredDataSources.map((dataSource) => {
                                const isSelected = selectedDataSources[dataSource.id] === true;
                                const hasAccess =
                                    requesting_access || dataSource.has_access !== false;

                                // Determine which icon to show and its properties
                                let iconElement = null;
                                let tooltipLabel = "";
                                let chipClickHandler = null;

                                if (hasAccess) {
                                    iconElement = (
                                        <IconCircleCheck
                                            size="0.875rem"
                                            color={isSelected ? "white" : "green"}
                                        />
                                    );
                                    tooltipLabel = "Access granted";
                                    chipClickHandler = () => {
                                        onDataSourceToggle(dataSource.id, !isSelected);
                                    };
                                } else if (pendingRequests.has(dataSource.id)) {
                                    iconElement = <IconClock size="0.875rem" color="#f59e0b" />;
                                    tooltipLabel = "Access request pending";
                                    chipClickHandler = null; // No action for pending
                                } else {
                                    iconElement = <IconExternalLink size="0.875rem" color="blue" />;
                                    tooltipLabel = "Request access";
                                    chipClickHandler = () => {
                                        setSelectedDatasource(dataSource);
                                        openAccessRequest();
                                    };
                                }

                                return (
                                    <Tooltip key={dataSource.id} label={tooltipLabel}>
                                        <Button
                                            onClick={chipClickHandler}
                                            size="xs"
                                            disabled={
                                                !hasAccess && pendingRequests.has(dataSource.id)
                                            }
                                            radius="sm"
                                            variant="filled"
                                            color={isSelected ? "violet" : "gray.2"}
                                            // compact
                                            styles={{
                                                root: {
                                                    cursor: chipClickHandler
                                                        ? "pointer"
                                                        : "not-allowed",
                                                    opacity: hasAccess ? 1 : 0.8,
                                                    height: "24px",
                                                    padding: "4px 8px",
                                                },
                                                inner: {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    fontWeight: isSelected ? 600 : 400,
                                                    fontSize: "12px",
                                                    color: isSelected ? "white" : "#666",
                                                },
                                            }}
                                        >
                                            {iconElement}
                                            <span style={{ marginLeft: 6 }}>{dataSource.name}</span>
                                        </Button>
                                    </Tooltip>
                                );
                            })}

                            {selectedDatasource && (
                                <DatasourceRequestAccessModal
                                    opened={accessRequestModalOpened}
                                    onClose={() => {
                                        closeAccessRequest();
                                    }}
                                    onSubmit={() => {
                                        setPendingRequests(
                                            new Set([...pendingRequests, selectedDatasource.id]),
                                        );
                                    }}
                                    title={`Request Access to ${selectedDatasource.name}`}
                                    requested_datasources={[selectedDatasource.id]}
                                />
                            )}
                        </Group>

                        {filteredDataSources.length > 1 && (
                            <Group gap={3} ml={4}>
                                <Text
                                    fw={11}
                                    c="dimmed"
                                    style={{
                                        cursor: "pointer",
                                        "&:hover": { color: "blue" },
                                        fontWeight: 500,
                                        opacity: 0.7,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        for (const ds of filteredDataSources) {
                                            const hasAccess =
                                                requesting_access || ds.has_access !== false;
                                            if (hasAccess) {
                                                onDataSourceToggle(ds.id, true);
                                            }
                                        }
                                    }}
                                >
                                    All
                                </Text>
                                <Text fw={11} c="dimmed" style={{ opacity: 0.5 }}>
                                    ·
                                </Text>
                                <Text
                                    fw={11}
                                    c="dimmed"
                                    style={{
                                        cursor: "pointer",
                                        "&:hover": { color: "blue" },
                                        fontWeight: 500,
                                        opacity: 0.7,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        for (const ds of filteredDataSources) {
                                            onDataSourceToggle(ds.id, false);
                                        }
                                    }}
                                >
                                    None
                                </Text>
                            </Group>
                        )}
                    </Stack>
                )}
            </Card>
        </div>
    );
}

function Questionnaire({
    heading_text = "Would you like to filter the search results by product line and data sources?",
    paragraph_text = `
    This is an optional filter to narrow down the search results, based on the product line and data sources you are interested in.
    You can request access to the data sources that you don't have access to just by clicking on the data source.
    `,
    button_text = "Save Preferences",
    requesting_access = false,
}) {
    const TRAINING_URL =
        "https://siemens-energy-learning.sabacloud.com/Saba/Web_spf/EU5P1PRD001/app/me/learningeventdetail/cours000000000184778)";

    const [selectedProductLine, setSelectedProductLine] = useState<string>("");
    const [dataSourceSelections, setDataSourceSelections] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);
    const [productLineDataSources, setProductLineDataSources] = useState<
        Record<string, Set<string>>
    >({});
    const navigate = useNavigate();
    const { data: user } = useUser();
    const location = useLocation();
    const { source, requesting_access: requestingAccessParam } = location.search as {
        source?: string;
        requesting_access?: string;
    };
    const account = useAccount();

    // Allow URL param to override the prop (for linking from widget)
    const isRequestingAccess = requestingAccessParam === "true" || requesting_access;

    // Compute display text based on isRequestingAccess mode
    const environment = import.meta.env.VITE_ENVIRONMENT;
    const displayHeadingText =
        isRequestingAccess && !requesting_access
            ? `Would you like to request access to Knowledge Search (${environment})?`
            : heading_text;
    const displayParagraphText =
        isRequestingAccess && !requesting_access
            ? "Please specify the data sources that you need access to, an admin will review your request."
            : paragraph_text;
    const displayButtonText =
        isRequestingAccess && !requesting_access ? "Request Access" : button_text;

    const initialized = useRef(false);
    const productLineInitialized = useRef(false);

    const [productLines, setProductLines] = useLocalStorage<Category[]>("availableProductLines", [
        { id: "all", title: "Default", subcategories: [] },
    ]);

    const [userDatasources, setUserDatasources] = useLocalStorage<Datasource[]>("datasources", []);

    const [chatPreferences, setChatPreferences] = useLocalStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>("chatPreferences", {
        selectedProductLines: [],
        selectedDatasources: [],
    });

    const [, setSelectedOrganization] = useLocalStorage<string>("selectedOrganization", "");

    const actualDatasources = userDatasources;
    const [, setSubmitting] = useState(false);
    const { instance } = useMsal();
    const queryClient = useQueryClient();

    const [requestReason, setRequestReason] = useState("");

    useEffect(() => {
        if (initialized.current) return;
        if (!user || !user.datasources || !user.organizations) return;
        if (user.info.user_preferences.datasources.length > 0) {
            // Avoid overriding user_preferences if user hasn't set anything yet
            localStorage.setItem(
                "memoryEnabled",
                String(user.info.user_preferences.memory_enabled),
            );
            setChatPreferences({
                selectedProductLines: [user.info.user_preferences.product_line || "all"],
                selectedDatasources: user.info.user_preferences.datasources,
            });
        }
        initialized.current = true;
        setUserDatasources(user.datasources);

        const productLineToDataSources: Record<string, Set<string>> = {};

        productLineToDataSources.all = new Set(user.datasources.map((ds) => ds.id));

        for (const org of user.organizations) {
            for (const productLine of org.product_lines) {
                productLineToDataSources[productLine] = new Set(
                    user.datasources
                        .filter((ds) => ds.product_line_filter.includes(productLine))
                        .map((ds) => ds.id),
                );
            }
        }

        setProductLineDataSources(productLineToDataSources);

        const orgOrder = [
            "GS D AGT",
            "GS D MGT",
            "GS D SGT",
            "GS C",
            "TI CP (RCE & TCP)",
            "TI STG",
        ];

        const orderedProductLines: string[] = [];
        const productLineToOrg: Record<string, string> = {};

        for (const orgId of orgOrder) {
            const org = user.organizations.find((o) => o.id === orgId);
            if (org && org.product_lines.length > 0) {
                const selectedProductLine = org.product_lines[org.product_lines.length - 1];
                orderedProductLines.push(selectedProductLine);
                productLineToOrg[selectedProductLine] = org.id;
            }
        }

        const productLinesData = [
            {
                id: "all",
                title: "Default",
                subcategories: [],
            },
            ...orderedProductLines.map((productLine) => {
                const orgId = productLineToOrg[productLine];
                const availableSources = user.datasources
                    .filter((ds) => ds.product_line_filter.includes(productLine))
                    .map((ds) => {
                        const name = ds.name
                            .replace(" Cases", "")
                            .replace(" Reports", "")
                            .replace(" Database", "");
                        return ds.has_access ? name : `${name} (No Access)`;
                    });

                return {
                    id: productLine,
                    title: orgId,
                    subcategories: availableSources,
                };
            }),
        ];

        setProductLines(productLinesData);
    }, [user, setUserDatasources, setProductLines, setChatPreferences]);

    useEffect(() => {
        if (productLineInitialized.current) return;
        if (!chatPreferences || Object.keys(productLineDataSources).length === 0) return;

        productLineInitialized.current = true;

        const initialProductLine =
            chatPreferences.selectedProductLines?.length > 0
                ? chatPreferences.selectedProductLines[0]
                : "all";

        setSelectedProductLine(initialProductLine);

        const initialSelections: Record<string, boolean> = {};

        if (productLineDataSources[initialProductLine]) {
            if (chatPreferences.selectedDatasources?.length > 0) {
                for (const dsId of chatPreferences.selectedDatasources) {
                    const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                    if (
                        productLineDataSources[initialProductLine].has(dsId) &&
                        datasource.has_access
                    ) {
                        initialSelections[dsId] = true;
                    }
                }

                if (Object.keys(initialSelections).length === 0) {
                    for (const dsId of productLineDataSources[initialProductLine]) {
                        const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                        initialSelections[dsId] = datasource.has_access;
                    }
                }
            } else {
                for (const dsId of productLineDataSources[initialProductLine]) {
                    const datasource = user?.datasources?.find((ds) => ds.id === dsId);
                    initialSelections[dsId] = datasource.has_access;
                }
            }
        }
        setDataSourceSelections(initialSelections);
    }, [chatPreferences, productLineDataSources, user]);

    const handleProductLineSelect = (categoryId: string) => {
        if (categoryId === selectedProductLine) return;

        setSelectedProductLine(categoryId);

        if (!productLineDataSources[categoryId]) {
            setDataSourceSelections({});
            return;
        }

        const freshSelections: Record<string, boolean> = {};
        for (const dsId of productLineDataSources[categoryId]) {
            const datasource = user?.datasources?.find((ds) => ds.id === dsId);
            freshSelections[dsId] = (isRequestingAccess || datasource?.has_access) ?? false;
        }

        setDataSourceSelections(freshSelections);
    };

    const handleDataSourceToggle = (dataSourceId: string, isChecked: boolean) => {
        setDataSourceSelections((prev) => {
            const newSelections = { ...prev };
            newSelections[dataSourceId] = isChecked;
            return newSelections;
        });
    };

    const handleRequestAccess = async () => {
        try {
            if (!account || !requestReason.trim()) return;
            setSubmitting(true);
            setLoading(true);
            const selectedDataSources = Object.keys(dataSourceSelections).filter(
                (key) => dataSourceSelections[key] === true,
            );
            const response = await postAPI({
                url: `${BACKEND_URL}/v1/user/access-requests`,
                payload: {
                    user_id:
                        account.idTokenClaims?.gid ||
                        account.idTokenClaims?.SCGID ||
                        account.idTokenClaims?.siemens_gid ||
                        account.localAccountId,
                    user_email: account.username,
                    requested_datasources: selectedDataSources,
                    reason: requestReason,
                },
                instance,
            });
            if (response.status >= 300) {
                throw new Error(
                    "Failed to request access, Please try again later or contact our product owner",
                );
            }
            notifications.show({
                title: "Saved",
                message:
                    "We'd review your request as soon as possible and get back to you, thanks for requesting access.",
            });

            // open the redirect link 2 seconds after submitting request
            setTimeout(() => {
                const newWindow = window.open(TRAINING_URL, "_blank", "noopener,noreferrer");

                if (newWindow) newWindow.opener = null;
                window.location.reload();
            }, 2000);
        } catch (error: unknown) {
            const err = error as {
                message?: string;
                response?: { data?: { error?: { message?: string } } };
            };
            notifications.show({
                title: "Error",
                color: "red",
                message:
                    err.message ??
                    err.response?.data?.error?.message ??
                    "Error submitting request.",
            });
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };
    const handleSavePreferences = async () => {
        const selectedDataSources = Object.keys(dataSourceSelections).filter(
            (key) => dataSourceSelections[key] === true,
        );

        let selectedOrgName = "";
        if (selectedProductLine && selectedProductLine !== "all") {
            for (const org of user?.organizations || []) {
                if (org.product_lines.includes(selectedProductLine)) {
                    selectedOrgName = org.id;
                    break;
                }
            }
        }

        const storedMemoryPreference = localStorage.getItem("memoryEnabled");
        const memoryPreference = storedMemoryPreference ? storedMemoryPreference === "true" : false;

        setLoading(true);

        try {
            await postAPI({
                url: `${BACKEND_URL}/v1/user/preferences`,
                payload: {
                    product_line: selectedProductLine.toUpperCase(),
                    datasources: selectedDataSources,
                    memory_enabled: memoryPreference,
                },
                instance,
                expectedStatus: 200,
            });

            setChatPreferences({
                selectedProductLines: selectedProductLine ? [selectedProductLine] : [],
                selectedDatasources: selectedDataSources,
            });

            setSelectedOrganization(selectedOrgName);

            // Notify any opener window (e.g. copilot widget) that preferences changed
            if (window.opener) {
                window.opener.postMessage({ type: "cts-preferences-updated" }, "*");
            }

            setTimeout(async () => {
                await queryClient.invalidateQueries({ queryKey: ["user"] });
                if (source) {
                    await navigate({ to: source });
                } else {
                    await navigate({ to: "/" });
                }
                setLoading(false);
            }, 100);
        } catch (error) {
            console.error("Error updating user preferences: ", error);
            notifications.show({
                title: "Error",
                color: "red",
                message: "Failed to save preferences. Please try again.",
            });
            setLoading(false);
        }
    };

    const getAvailableDataSourcesForProductLine = (productLine: string): Set<string> => {
        return productLineDataSources[productLine] || new Set();
    };
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                setPendingRequests(await fetchPendingRequests(instance));
            } catch (err) {
                console.error("Failed to fetch pending requests:", err);
            }
        };
        fetchRequests();
    }, [instance]);

    return (
        <main className="mx-auto p-4">
            <Container size="xs">
                <div>
                    <Text size="xl" fw={600}>
                        {displayHeadingText}
                    </Text>
                    <Text size="sm" c="dimmed" mt={24} mb={24} style={{ whiteSpace: "pre-line" }}>
                        {displayParagraphText}
                    </Text>

                    <Stack gap="sm">
                        {productLines.map((category, index) => (
                            <RadioCategoryItem
                                key={category.id}
                                category={category}
                                selected={selectedProductLine === category.id}
                                onSelect={handleProductLineSelect}
                                index={index}
                                dataSources={actualDatasources}
                                selectedDataSources={dataSourceSelections}
                                onDataSourceToggle={handleDataSourceToggle}
                                availableDataSourcesForProductLine={getAvailableDataSourcesForProductLine(
                                    category.id,
                                )}
                                requesting_access={isRequestingAccess}
                                pendingRequests={pendingRequests}
                                setPendingRequests={setPendingRequests}
                            />
                        ))}
                        <Textarea
                            required
                            label="Reason for access:"
                            placeholder="Example: I need it to help me with my daily customer support work..."
                            style={{ flex: 1 }}
                            size="xl"
                            value={requestReason}
                            onChange={(event) => setRequestReason(event.currentTarget.value)}
                            styles={{
                                root: { display: isRequestingAccess ? "block" : "none" },
                                label: { fontSize: "16px" },
                                input: { fontSize: "16px" },
                            }}
                        />
                    </Stack>

                    <Group justify="flex-end" mt={24}>
                        <Button
                            onClick={
                                isRequestingAccess ? handleRequestAccess : handleSavePreferences
                            }
                            disabled={
                                loading ||
                                !selectedProductLine ||
                                (isRequestingAccess && !requestReason.trim()) ||
                                Object.values(dataSourceSelections).every((selected) => !selected)
                            }
                            loading={loading}
                            style={(theme) => ({
                                "&:disabled": {
                                    backgroundColor: theme.colors.gray[2],
                                    color: theme.colors.gray[5],
                                },
                            })}
                        >
                            {loading ? "Submitting..." : displayButtonText}
                        </Button>
                    </Group>
                </div>

                <style>
                    {`
                    @keyframes revealBottom {
                      from {
                        opacity: 0;
                        transform: translateY(20px);
                      }
                      to {
                        opacity: 1;
                        transform: translateY(0);
                      }
                    }
                  `}
                </style>
            </Container>
        </main>
    );
}

export const Route = createFileRoute("/_layout-nosidebar/questionnaire")({
    component: Questionnaire,
});
export default Questionnaire;
